import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin } from "lucide-react";
import api from "@/lib/api";
import PropertyCard, { formatPrice } from "@/components/PropertyCard";
import MapView from "@/components/MapView";

// Famous landmark photo for each city (verified working Unsplash URLs)
const CITY_CENTER = {
  Lucknow: { lat: 26.8467, lng: 80.9462 },
  Gurugram: { lat: 28.4595, lng: 77.0266 },
  Noida: { lat: 28.5355, lng: 77.3910 },
  Delhi: { lat: 28.6139, lng: 77.2090 },
  Mumbai: { lat: 19.0760, lng: 72.8777 },
  Bangalore: { lat: 12.9716, lng: 77.5946 },
  Pune: { lat: 18.5204, lng: 73.8567 },
  Hyderabad: { lat: 17.3850, lng: 78.4867 },
  Dubai: { lat: 25.2048, lng: 55.2708 },
  Singapore: { lat: 1.3521, lng: 103.8198 },
};

const HERO_BY_CITY = {
  Lucknow: {
    image: "https://images.unsplash.com/photo-1611916656173-875e4277bea6?w=2400",
    landmark: "Bara Imambara · City of Nawabs",
    blurb: "City of Nawabs — heritage, food, and India's fastest-growing tier-2 realty market.",
  },
  Gurugram: {
    image: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=2400",
    landmark: "Cyber Hub Skyline",
    blurb: "India's millennium city — Fortune 500 HQs, golf course living and ultra-luxury condominiums.",
  },
  Noida: {
    image: "https://images.unsplash.com/photo-1564507592333-c60657eea523?w=2400",
    landmark: "Taj Corridor · Yamuna Expressway",
    blurb: "Planned, well-connected, and the new HNI address along the Yamuna Expressway — three hours from the Taj.",
  },
  Delhi: {
    image: "https://images.unsplash.com/photo-1587474260584-136574528ed5?w=2400",
    landmark: "India Gate · The Capital",
    blurb: "The capital — heritage colonies, central districts and rare freehold luxury.",
  },
  Mumbai: {
    image: "https://images.unsplash.com/photo-1666843527155-14ec5f016802?w=2400",
    landmark: "Marine Drive · Queen's Necklace",
    blurb: "India's financial capital — sea-facing residences, Bandra-Worli sea-link views and the country's deepest realty market.",
  },
  Bangalore: {
    image: "https://images.unsplash.com/photo-1709967884183-7ffa9d168508?w=2400",
    landmark: "Vidhana Soudha · Garden City",
    blurb: "Silicon Valley of India — gated villa communities, ORR tech parks and steady year-on-year appreciation.",
  },
  Pune: {
    image: "https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=2400",
    landmark: "Shaniwar Wada · Oxford of the East",
    blurb: "Oxford of the East — IT corridor expansion across Hinjewadi, Kharadi and Wakad.",
  },
  Hyderabad: {
    image: "https://images.unsplash.com/photo-1601581875309-fafbf2d3ed3a?w=2400",
    landmark: "Charminar · Pearl City",
    blurb: "Pearl city of India — HITEC corridor and Banjara Hills make this the calmest metro for HNI buyers.",
  },
  Dubai: {
    image: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=2400",
    landmark: "Burj Khalifa & Downtown",
    blurb: "Tax-free, currency-pegged, golden-visa real estate within 3 hours of every Indian metro.",
  },
  Singapore: {
    image: "https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=2400",
    landmark: "Marina Bay Sands",
    blurb: "Asia's safest property market — freehold scarcity, perpetual demand from global capital.",
  },
};

export default function CityPage() {
  const { city } = useParams();
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get(`/projects?city=${city}`).then((r) => setProjects(r.data || []));
    api.get("/projects/cities/stats").then((r) => {
      const s = (r.data || []).find((x) => x.city === city);
      setStats(s);
    });
  }, [city]);

  const meta = HERO_BY_CITY[city] || HERO_BY_CITY.Lucknow;

  return (
    <div data-testid="city-page" className="pb-32">
      <section className="relative h-[65vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={meta.image} alt={`${city} - ${meta.landmark}`} className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-12 pb-12">
          <Link to="/" className="text-xs tracking-[0.2em] uppercase text-slate-700 hover:text-[#D4A437] inline-flex items-center gap-2 mb-6">
            <ArrowLeft className="w-3.5 h-3.5" /> Home
          </Link>
          <div className="overline mb-3 flex items-center gap-2">
            <MapPin className="w-3 h-3" /> {meta.landmark}
          </div>
          <h1 className="font-serif text-5xl sm:text-7xl text-slate-900 font-light">{city}</h1>
          <p className="text-slate-700 text-lg mt-4 max-w-2xl font-light">{meta.blurb}</p>
        </div>
      </section>

      {stats && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 -mt-12 relative z-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-100 border border-slate-100">
            <div className="bg-white p-6"><div className="overline mb-1">Avg / sqft</div><div className="font-serif text-2xl text-[#D4A437]">{stats.avg_price_per_sqft?.toLocaleString()}</div></div>
            <div className="bg-white p-6"><div className="overline mb-1">Min Price</div><div className="font-serif text-2xl text-slate-900">{formatPrice(stats.min_price)}</div></div>
            <div className="bg-white p-6"><div className="overline mb-1">Max Price</div><div className="font-serif text-2xl text-slate-900">{formatPrice(stats.max_price)}</div></div>
            <div className="bg-white p-6"><div className="overline mb-1">Projects</div><div className="font-serif text-2xl text-slate-900">{stats.count}</div></div>
          </div>
        </section>
      )}

      {projects.some((p) => p.lat != null) ? (
        <section className="max-w-7xl mx-auto px-6 md:px-12 mt-20">
          <div className="overline mb-3">Map view</div>
          <h2 className="font-serif text-2xl text-slate-900 mb-6">Where they sit on the map</h2>
          <MapView
            height={420}
            zoom={11}
            markers={projects.map((p) => ({
              lat: p.lat, lng: p.lng, name: p.name, locality: p.locality, city: p.city,
              price: formatPrice(p.price_min, p.currency),
            }))}
          />
        </section>
      ) : CITY_CENTER[city] ? (
        <section className="max-w-7xl mx-auto px-6 md:px-12 mt-20">
          <div className="overline mb-3">Map view</div>
          <h2 className="font-serif text-2xl text-slate-900 mb-6">{city} on the map</h2>
          <MapView
            height={420}
            zoom={11}
            center={CITY_CENTER[city]}
            markers={[{ ...CITY_CENTER[city], name: city, locality: city, city }]}
          />
        </section>
      ) : null}

      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-20">
        <h2 className="font-serif text-3xl text-slate-900 mb-8">Projects in {city}</h2>
        {projects.length === 0 ? (
          <div className="text-slate-500 py-12">No projects available in {city} yet.</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((p) => <PropertyCard key={p.id} project={p} />)}
          </div>
        )}
      </section>
    </div>
  );
}
